import { all, call, debounce, delay, fork, put, select, take, takeEvery, takeLatest } from "redux-saga/effects";

import {
    bridge,
    FAVORITE_FORM_UPDATED,
    FAVORITE_FORMS_LOADED,
    FORM_CLOSED,
    FORM_ITEM_INSTANCE_CLOSE_RESTRICTIONS_UPDATED,
    FORM_SWITCHED,
    FormOpenMethod,
    HISTORY_FORMS_INFO_UPDATED,
    IOpenFormAction,
    ISwitchToFormAction,
    OPEN_FORM,
    SWITCH_TO_FORM,
} from "@multiweb/reactCore/bridge";
import { createAreLocalizationKeysLoadedActionPattern } from "@multiweb/reactCore/i18n";
import { EMPTY_ARRAY, EVENT_POSTPROCESS_DELAY, invariant, Key } from "@multiweb/reactUtils";
import { NodeDefinitionConfig } from "@multiweb/types/multiweb";
import { IAppState, SagaWithReturnType, SagaYieldReturn } from "@multiweb/types/react";
import { createMenuItemActivatedAction, createSetVisualAutomatStateAction, createToggleSubmenuAction } from "../actions";
import {
    CONCAT_NOTIFICATION_ITEMS,
    FETCHING_KEY_FORM_ITEMS_LOCALIZATION,
    FORM_SEARCH_INPUT_ID,
    RESET_NOTIFICATION_ITEMS,
    SET_FORM_ITEM_ACTION_RESTRICTIONS,
    SET_GROUP_NOTIFICATIONS_AS_READ_BUTTON_ID,
    SET_MENU_ITEMS,
    SET_NOTIFICATION_GROUPS,
    SET_PROFILE_ACTION_RESTRICTIONS,
    SHOW_ALL_NOTIFICATIONS_BUTTON_ID,
    USER_MENU_LOGOUT_ITEM_ID,
    USER_MENU_SETTINGS_ITEM_ID,
    USER_MENU_TOGGLE_THEME_ITEM_ID,
    VISUAL_AUTOMAT_PROCESSED,
} from "../constants";
import { getOrderedFavoriteMenuItems } from "../favoriteItems";
import { getOpenedFormsHistoryInfo } from "../historyItems";
import { logger } from "../logger";
import { getOpenedFormInstanceId, getOpenedFormsIds } from "../openedItems";
import { MenuPanelItemId } from "../panelItems";
import { getSearchResults, isSearchActive, isSearchActive as isSearchActiveSelector, SET_SEARCH_RESULTS } from "../search";
import {
    getActiveSectionId,
    getAllowedRootFormItemIds,
    getNotificationGroupIds,
    getNotificationItemIds,
    isCombinedItem,
    isMenuExpanded,
    isMenuFloated,
    isMenuVisible,
    isNotificationGroupId,
    isNotificationGroupPanelItemId,
} from "../selectors";
import { IMenuItemActivatedAction, MenuItemActivatedType } from "../types";
import { createFormPanelItemId, createNotificationGroupPanelItemId, formOpenedAndShowInMenuActionPattern } from "../utils";
import {
    createClearFocusedItemAction,
    createClearNavigationSectionAction,
    createNavigationNodesUpdatedAction,
    createSetFocusedItemAction,
    createSetNavigationNodesAction,
} from "./actions";
import { ACTION_ITEM_FOCUSED, FOCUS_DEFAULT_ITEM, KEY_PRESSED, MENU_FOCUS_LOST } from "./constants";
import { FORM_SEARCH_INPUT_FOCUSABLE_ID, QUICK_ACCESS_FOCUSABLE_ID, USER_MENU_FOCUSABLE_ID } from "./menuNavigationConstants";
import {
    createFormMenuNavigationNodes,
    createNotificationMenuNavigationNodes,
    createQuickAccessMenuNavigationNodes,
    createSearchResultNavigationNodes,
} from "./menuNavigationUtils";
import {
    getFirstQuickAccessFocusableItemId,
    getFocusedItemId,
    getFocusedItemStoreSection,
    getFocusedNavigationNode,
    getLastQuickAccessFocusableItemId,
    getNavigationNode,
    getSectionNavigationNode,
} from "./selectors";
import {
    CompleteNavigationConfig,
    FocusableItemId,
    IActionItemFocusedAction,
    IKeyPressedAction,
    IMenuFocusLostAction,
    MenuFocusableItemType,
    MenuNavigationSectionType,
    MutableNavigationNode,
    NavigationNode,
    NavigationSection,
} from "./types";
import {
    connectMultipleToEsc,
    connectMultipleToLeftNode,
    connectMultipleVertically,
    connectMultipleVerticallyCyclic,
    connectVertically,
    copyNode,
    createFocusableItemId,
    createMenuItemActivatedByMousePattern,
    createMultipleNavigationNodes,
    createNavigationNode,
    getNextFocusedItemId,
    menuItemActivatedTypeToFocusableItemType,
    mergeNavigationConfig,
} from "./utils";

const { getMenuNavigationConfig } = bridge;

export const saga: SagaWithReturnType = function* navigationSaga() {
    const customConfig: SagaYieldReturn<typeof getMenuNavigationConfig> = yield call(getMenuNavigationConfig);
    const navigationConfig: SagaYieldReturn<typeof mergeNavigationConfig> = yield call(mergeNavigationConfig, customConfig, DEFAULT_NAVIGATION_CONFIG);
    if (!navigationConfig.enableNavigation) {
        logger.info("Navigation submodule is disabled by custom configuration.");
        return;
    }
    logger.info("Navigation submodule saga has been started.");

    yield fork(navigationNodeTreeSaga, navigationConfig);
    yield fork(actionItemMouseFocusSaga);
    yield takeEvery(KEY_PRESSED, keyNavigationSaga, navigationConfig);
    yield takeLatest(FOCUS_DEFAULT_ITEM, defaultFocusSaga);
    yield takeLatest(MENU_FOCUS_LOST, menuFocusLostSaga);

    if (navigationConfig.optionalFeatures.enableAutoFocusOpenedForms) {
        yield takeLatest(OPEN_FORM, updateFocusOnFormOpenedSaga);
        yield takeLatest(SWITCH_TO_FORM, updateFocusOnFormSwitchedSaga);
    }
};

/**
 * Nastaví zaměření na uzel se zadaným id, pokud již není zaměřený. Může ušetřit zbytečné vyslání akce, pokud je již požadovaný uzel zaměřený.
 * @param focusableItemId Id uzlu, který se má zaměřit.
 */
export const focusIfNotFocusedSaga: SagaWithReturnType<[FocusableItemId]> = function* focusIfNotFocusedSaga(focusableItemId) {
    const focusedItemId: ReturnType<typeof getFocusedItemId> = yield select(getFocusedItemId);
    if (focusedItemId !== focusableItemId) {
        yield put(createSetFocusedItemAction(focusableItemId));
    }
};

/**
 * Obsluhuje navigaci pomocí klávesnice.
 * @param navigationConfig Nastavení navigačního modulu.
 * @param action Akce stisku klávesy.
 */
export const keyNavigationSaga: SagaWithReturnType<[CompleteNavigationConfig, IKeyPressedAction]> = function* keyNavigationSaga(navigationConfig, action) {
    const focusedNavigationNode: ReturnType<typeof getFocusedNavigationNode> = yield select(getFocusedNavigationNode);
    if (!focusedNavigationNode) {
        return;
    }
    const nextFocusedItemId: ReturnType<typeof getNextFocusedItemId> = yield call(getNextFocusedItemId, action.payload.event, focusedNavigationNode);
    if (!nextFocusedItemId) {
        return;
    }
    yield put(createSetFocusedItemAction(nextFocusedItemId));

    if (navigationConfig.optionalFeatures.enableSubmenuAutoOpenAndClose) {
        yield call(submenuAutoCloseSaga, action, focusedNavigationNode);
        yield call(submenuRightArrowActivationSaga, action, focusedNavigationNode);
    }
    if (navigationConfig.optionalFeatures.enableMenuAutoClose) {
        yield call(menuAutoCloseSaga, action, nextFocusedItemId);
    }
};

/**
 * Obsluhuje nastavení výchozí zaměřené položky menu. Používá zpoždění před nastavením hodnoty. Řeší se tím případ, kdy menu získá zaměření pomocí kliknutí myší na
 * konkrétní položku. Jelikož je již zaměřená položka, na kterou se kliklo, není třeba nastavovat výchozí hodnotu. Jelikož však událost získání zaměření přijde dříve,
 * než se ve stavu aktualizuje zaměřená položka, je třeba použít zpoždění.
 */
export const defaultFocusSaga: SagaWithReturnType = function* defaultFocusSaga() {
    yield delay(EVENT_POSTPROCESS_DELAY);
    const focusedItemId: ReturnType<typeof getFocusedItemId> = yield select(getFocusedItemId);
    if (!focusedItemId) {
        yield put(createSetFocusedItemAction(createFocusableItemId(
            MenuPanelItemId.quickAccess,
            MenuFocusableItemType.panelItem,
            MenuNavigationSectionType.static,
        )));
    }
};

/**
 * Obsluhuje ztrátu zaměření menu. Pokud se přesouvá zaměření v rámci dokumentu vyčistí zaměřený prvek v menu, aby se při opětovném získání zaměření začínalo
 * s výchozím zaměřeným prvkem. Pokud se zaměření přesouvá mimo dokument (např. na jinou záložku prohlížeče), zachová původně zaměřenou položku, aby se po získání
 * zaměření pokračovalo tam, kde se skončilo.
 * @param action Akce ztráty zaměření.
 */
export const menuFocusLostSaga: SagaWithReturnType<[IMenuFocusLostAction]> = function* menuFocusLostSaga(action) {
    if (!action.payload.documentHidden) {
        yield put(createClearFocusedItemAction());
    }
};

/**
 * Nastaví zaměření na aktivní sekci.
 */
export const setFocusToActiveSectionSaga: SagaWithReturnType = function* setFocusToActiveSectionSaga() {
    const activeSectionId: ReturnType<typeof getActiveSectionId> = yield select(getActiveSectionId);
    const sectionNode: ReturnType<typeof getSectionNavigationNode> = yield select(getSectionNavigationNode, activeSectionId);
    const focusedItemId = yield select(getFocusedItemId);
    if (sectionNode && focusedItemId !== sectionNode.self.id) {
        yield put(createSetFocusedItemAction(sectionNode.self.id));
    }
};

/**
 * Obsluhuje zaměření nově otevřeného formuláře v sekci Rychlý přístup, pokud je menu otevřené. Pokud je menu ve float módu, přenese zaměření na aktivní sekci.
 * @param action Akce žádosti o otevření formuláře.
 */
export const updateFocusOnFormOpenedSaga: SagaWithReturnType<[IOpenFormAction]> = function* updateFocusOnFormOpenedSaga(action) {
    const menuItemId = action.payload.openFormData?.menuItemId;
    const isCombinedItemType = menuItemId && (yield select(isCombinedItem, menuItemId));
    if (isCombinedItemType) { // kombinované položky otevírají kromě formuláře i menu, proto se nepřepíná na sekci Rychlý přístup a ani není třeba tu měnit focus
        return;
    }

    if (action.payload.openMethod === FormOpenMethod.inTab) {
        // Pokud se formulář otvírá v nové záložce, tak v této záložce už nedorazí akce dokončení operace. Proto se čeká pouze na automat.
        yield take(VISUAL_AUTOMAT_PROCESSED);
    } else {
        yield all([
            take([formOpenedAndShowInMenuActionPattern, FORM_SWITCHED]), // Požadavek na otevření formuláře může skončit otevřením nebo přepnutím. Je třeba sledovat obě akce.
            take(VISUAL_AUTOMAT_PROCESSED),
        ]);
    }

    if (yield select(isMenuVisible)) {
        const openedFormInstanceId: ReturnType<typeof getOpenedFormInstanceId> = yield select(getOpenedFormInstanceId);
        if (openedFormInstanceId) {
            const searchIsActive: ReturnType<typeof isSearchActiveSelector> = yield select(isSearchActiveSelector);
            const section = searchIsActive ? MenuNavigationSectionType.searchResultsOpened : MenuNavigationSectionType.quickAccessOpened;
            const newFocusedId: SagaYieldReturn<typeof createFocusableItemId> = yield call(createFocusableItemId, openedFormInstanceId, MenuFocusableItemType.openedItem, section);
            yield call(focusIfNotFocusedSaga, newFocusedId);
        }
    } else {
        yield call(setFocusToActiveSectionSaga);
    }
};

/**
 * Obsluhuje zaměření přepnutého formuláře v sekci historie.
 * @param action Akce přepnutí aktivního formuláře.
 */
export const updateFocusOnFormSwitchedSaga: SagaWithReturnType<[ISwitchToFormAction]> = function* updateFocusOnFormSwitchedSaga(action) {
    yield all([
        take(FORM_SWITCHED),
        take(VISUAL_AUTOMAT_PROCESSED),
    ]);
    if (yield select(isMenuVisible)) {
        const { formInstanceId } = action.payload;
        const searchIsActive: ReturnType<typeof isSearchActiveSelector> = yield select(isSearchActiveSelector);
        const section = searchIsActive ? MenuNavigationSectionType.searchResultsOpened : MenuNavigationSectionType.quickAccessOpened;
        const newFocusedId: ReturnType<typeof createFocusableItemId> = yield call(createFocusableItemId, formInstanceId, MenuFocusableItemType.openedItem, section);
        yield call(focusIfNotFocusedSaga, newFocusedId);
    } else {
        yield call(setFocusToActiveSectionSaga);
    }
};

/**
 * Obsluhuje aktivaci submenu klávesou pravé šipky.
 * Nelze použít přímé volání otevření submenu, protože výchozí chování může být změněno v modulu custom akcí. Proto musí být použita akce obecné aktivace.
 * @param action Akce stisku klávesy.
 * @param focusedNavigationNode Navigační uzel zaměřený v době stisku klávesy.
 */
export const submenuRightArrowActivationSaga: SagaWithReturnType<[IKeyPressedAction, NavigationNode]> = function* submenuRightArrowActivationSaga(action, focusedNavigationNode) {
    const { event } = action.payload;
    const { itemId, type, section } = focusedNavigationNode.self;
    if (type === MenuFocusableItemType.folderItem && section === MenuNavigationSectionType.forms && event.key === Key.ArrowRight) {
        yield put(createMenuItemActivatedAction(itemId, MenuItemActivatedType.formFolder, MenuNavigationSectionType.forms, undefined, event));
    }
};

/**
 * Obsluhuje automatické zavírání submenu při navigaci klávesnicí v menu formulářů.
 * Reaguje na klávesu escape a levou šipku.
 * @param action Akce stisku klávesy
 * @param actionSourceNavigationNode Navigační uzel položky, na které se stiskla klávesa.
 */
export const submenuAutoCloseSaga: SagaWithReturnType<[IKeyPressedAction, NavigationNode]> = function* submenuAutoCloseSaga(action, actionSourceNavigationNode) {
    if (actionSourceNavigationNode.self.section !== MenuNavigationSectionType.forms) {
        return;
    }
    const { key } = action.payload.event;
    if (key !== Key.Escape && key !== Key.ArrowLeft) {
        return;
    }
    const targetFocusableId = key === Key.Escape ? actionSourceNavigationNode.escId : actionSourceNavigationNode.leftId;
    if (!targetFocusableId) {
        return;
    }
    const targetNode: ReturnType<typeof getNavigationNode> = yield select(getNavigationNode, targetFocusableId);
    if (!targetNode || targetNode.self.type !== MenuFocusableItemType.folderItem || targetNode.self.section === MenuNavigationSectionType.panelForms) {
        return;
    }
    yield put(createToggleSubmenuAction(targetNode.self.itemId as number));
};

/**
 * Obsluhuje zavírání menu při navigaci klávesnicí ve float režimu menu.
 * @param action
 * @param nextFocusedItemId
 */
export const menuAutoCloseSaga: SagaWithReturnType<[IKeyPressedAction, FocusableItemId]> = function* menuAutoCloseSaga(action, nextFocusedItemId) {
    const menuIsFloating: ReturnType<typeof isMenuFloated> = yield select(isMenuFloated);
    const { key } = action.payload.event;
    if (menuIsFloating && (key === Key.Escape || key === Key.ArrowLeft)) {
        const newFocusedNode: ReturnType<typeof getNavigationNode> = yield select(getNavigationNode, nextFocusedItemId);
        if (newFocusedNode?.self.type === MenuFocusableItemType.panelItem) {
            yield put(createSetVisualAutomatStateAction("000"));
        }
    }
};

/**
 * Obsluhuje aktualizaci zaměření při změně aktivní sekce. Nastaví zaměření na první položku aktivní sekce. Pokud sekce nemá žádné položky, nastaví zaměření na
 * položku sekce jako takové.
 *
 * @param menuPanelItemId Identifikátor položky panelu menu
 */
export const updateSelectedSectionNavigationSaga: SagaWithReturnType<[string]> = function* updateSelectedSectionNavigationSaga(menuPanelItemId) {
    const sectionNode: ReturnType<typeof getSectionNavigationNode> = yield select(getSectionNavigationNode, menuPanelItemId);
    if (!sectionNode) {
        yield call(logger.error, "Cannot find keyboard navigation node for section with id: %s", menuPanelItemId);
        return;
    }

    let newFocusedItemId: FocusableItemId;
    const menuIsVisible = yield select(isMenuVisible);
    const isNotificationGroup = sectionNode.self.section === MenuNavigationSectionType.notificationGroups;
    if (!menuIsVisible) { // pokud bylo menu po aktivaci sekce skryto, zaměřujeme vždy položku sekce
        newFocusedItemId = sectionNode.self.id;
    } else if (isNotificationGroup) {
        /* Sekce notifikací nesmějí používat pravého souseda. */
        newFocusedItemId = sectionNode.self.id;
    } else {
        newFocusedItemId = sectionNode.rightId || sectionNode.self.id;
    }

    yield put(createSetFocusedItemAction(newFocusedItemId));
};

/**
 * Čas v ms pro debounce při generování uzlů. Debounce je nutný z důvodu udržení konzistence. Sága generující uzly může být spuštěna více akcemi, jenže není atomická.
 * Sága tak může běžet vícekrát najednou a jejich efekty se mohou prolínat, což vede k nekonzistenci výsledného stavu. Debounce zabrání souběžnému spuštění v daném
 * intervalu a navíc funguje jako optimalizace, aby generování neprobíhalo několikrát za sebou.
 *
 * Stanovená hodnota je experimentální.
 */
const NODE_GENERATION_DEBOUNCE = 20;

/**
 * Obsluhuje tvorbu a aktualizace navigačního stromu pro navigaci pomocí klávesnice v menu.
 *
 * Implementační poznámka:
 * Využívá rozgenerování (https://redux-saga.js.org/docs/advanced/SequencingSagas.html) ságy {@link setupNodeGenerationSaga} pomocí operátoru yield*. To umožňuje zajistit, aby call efekty pro generování statických uzlů
 * probíhaly sekvenčně a zároveň aby se zpracovalo generování statických i dynamických uzlů pro danou část v jedné sáze. Sekvenční volání pro statické uzly je
 * nutné kvůli závislostem mezi sekcemi a propojování jejich uzlů. Např. generování uzlů uživatelského menu vyžaduje existenci uzlu User menu z menu panelu, aby
 * se na něj mohly napojit.
 *
 * Alternativou by bylo nespouštět generování po sekcích, ale ze sekcí vyfiltrovat zvlášť statické a dynamické části a spouštět je zvlášť.
 *
 * @param navigationConfig Konfigurace navigace.
 */
export const navigationNodeTreeSaga: SagaWithReturnType<[CompleteNavigationConfig]> = function* navigationNodeTreeSaga(navigationConfig) {
    const { menuPanel, quickAccess, notificationList, searchResults, userMenu } = navigationConfig.nodeDefinitions;

    yield* setupNodeGenerationSaga(menuPanel);
    yield* setupNodeGenerationSaga(userMenu);
    yield* setupNodeGenerationSaga(quickAccess);
    yield* setupNodeGenerationSaga(searchResults);
    yield* setupNodeGenerationSaga(notificationList);
};

/**
 * Nastaví generování uzlů podle zadané konfigurace sekce.
 * Pokud je definovaná sága pro statické uzly, spustí ji rovnou v blokujícím režimu.
 * Pokud je definované dynamické generování uzlů, nastaví poslouchání akcí přes debounce. Použitá hodnota pro debounce je {@link NODE_GENERATION_DEBOUNCE}.
 *
 * Sága je určená pro rozgenerování pomocí operátoru yield*, viz dokumentace {@link navigationNodeTreeSaga}.
 *
 * @param nodeDefinitionConfig Nastavení pro generování uzlů.
 */
export const setupNodeGenerationSaga: SagaWithReturnType<[NodeDefinitionConfig]> = function* (nodeDefinitionConfig) {
    const { staticSaga, dynamic } = nodeDefinitionConfig;
    if (staticSaga) {
        yield call(staticSaga);
    }
    if (dynamic) {
        const { actionPatterns, saga } = dynamic;
        invariant(actionPatterns.length > 0, "[MainMenu] Action pattern array for dynamic navigation node generation cannot be empty.");
        // Typování pro debounce nezvládá readonly variantu, proto není možné použít actionPatterns přímo
        yield debounce(NODE_GENERATION_DEBOUNCE, [...actionPatterns], saga);
    }
};

/**
 * Obsluhuje tvorbu navigačních uzlů nezávislých na datech pro sekci Rychlý přístup (ve stavu neaktivního vyhledávání).
 * @see createQuickAccessNavigationNodesSaga
 */
export const createStaticQuickAccessNavigationNodesSaga: SagaWithReturnType = function* createStaticQuickAccessNavigationNodesSaga() {
    const quickAccessNode: SagaYieldReturn<typeof getNavigationNode> = yield select(getNavigationNode, QUICK_ACCESS_FOCUSABLE_ID);
    invariant(quickAccessNode, `[MainMenu] Cannot create navigation nodes for Quick Access, because node for menu panel item Quick Access (${QUICK_ACCESS_FOCUSABLE_ID}) does not exist.`);

    const newQuickAccessNode: SagaYieldReturn<typeof copyNode> = yield call(copyNode, quickAccessNode);
    const searchInputNode: SagaYieldReturn<typeof createNavigationNode> = yield call(createNavigationNode, FORM_SEARCH_INPUT_ID, MenuFocusableItemType.searchInput, MenuNavigationSectionType.static);
    newQuickAccessNode.rightId = searchInputNode.self.id;
    searchInputNode.escId = newQuickAccessNode.self.id;
    yield put(createSetNavigationNodesAction(MenuNavigationSectionType.static, searchInputNode, newQuickAccessNode));
};

/**
 * Obsluhuje tvorbu navigačních uzlů závislých na datech pro sekci Rychlý přístup (ve stavu neaktivního vyhledávání).
 * @see createStaticQuickAccessNavigationNodesSaga
 */
export const createQuickAccessNavigationNodesSaga: SagaWithReturnType = function* createQuickAccessNavigationNodesSaga() {
    // 1. získání sousedních uzlů z panelu menu + kontrola
    const quickAccessNavigationNode: ReturnType<typeof getNavigationNode> = yield select(getNavigationNode, QUICK_ACCESS_FOCUSABLE_ID);
    if (!quickAccessNavigationNode) {
        yield call(logger.error, "Cannot find navigation node for id: %s", QUICK_ACCESS_FOCUSABLE_ID);
        return;
    }

    const oldSearchInputNode: ReturnType<typeof getNavigationNode> = yield select(getNavigationNode, FORM_SEARCH_INPUT_FOCUSABLE_ID);
    if (!oldSearchInputNode) {
        yield call(logger.error, "Cannot find navigation node for id: %s", FORM_SEARCH_INPUT_FOCUSABLE_ID);
        return;
    }
    const newSearchInputNode: SagaYieldReturn<typeof copyNode> = yield call(copyNode, oldSearchInputNode);

    // 2. získání položek v pořadí vykreslení
    const openedFormIds: ReturnType<typeof getOpenedFormsIds> = yield select(getOpenedFormsIds);
    const openedFormsHistoryInfo: ReturnType<typeof getOpenedFormsHistoryInfo> = yield select(getOpenedFormsHistoryInfo);
    const favoriteForms: ReturnType<typeof getOrderedFavoriteMenuItems> = yield select(getOrderedFavoriteMenuItems);

    // 3. vytvoření navigačních uzlů
    const state: IAppState = yield select();
    const quickAccessNodes: ReturnType<typeof createQuickAccessMenuNavigationNodes> = yield call(createQuickAccessMenuNavigationNodes, openedFormIds, openedFormsHistoryInfo, favoriteForms, quickAccessNavigationNode, newSearchInputNode, state);

    // 4. vložení uzlů do stavu
    const searchIsActive: ReturnType<typeof isSearchActive> = yield select(isSearchActive);
    // Vazby na pole vyhledávání nepřepisujeme, pokud je aktivní vyhledávání. V takovém případě si to řeší sága pro uzly vyhledávání.
    const additionalNodes = searchIsActive ? EMPTY_ARRAY : [newSearchInputNode];
    yield call(setNavigationNodesForSectionSaga, MenuNavigationSectionType.quickAccess, quickAccessNodes, quickAccessNavigationNode, MenuNavigationSectionType.static, additionalNodes);
};

/**
 * Zajišťuje synchronizaci mezi generováním uzlů pro formuláře a pro notifikační skupiny a jejich následné propojení v menu panelu.
 */
export const createFormsAndNotificationGroupsNavigationNodesSaga: SagaWithReturnType = function* createFormsAndNotificationGroupsNavigationNodesSaga() {
    const rootFormNodes: SagaYieldReturn<typeof createFormsNavigationNodesSaga> = yield call(createFormsNavigationNodesSaga);
    const notificationGroupNodes: SagaYieldReturn<typeof createNotificationGroupsNavigationNodesSaga> = yield call(createNotificationGroupsNavigationNodesSaga);
    yield call(connectPanelItemFormsAndNotificationGroupsSaga, rootFormNodes, notificationGroupNodes);
};

/**
 * Propojí a nastaví uzly formulářů a notifikačních skupin v menu panelu.
 * @param rootFormNodes Navigační uzly formulářů pro menu panel.
 * @param notificationGroupNodes Navigační uzly notifikačních skupin.
 */
export const connectPanelItemFormsAndNotificationGroupsSaga: SagaWithReturnType<[readonly MutableNavigationNode[], readonly MutableNavigationNode[]]> = function* connectPanelItemFormsAndNotificationGroupsSaga(
    rootFormNodes,
    notificationGroupNodes,
) {
    // 1. získání sousedních uzlů z panelu menu + kontrola
    const oldQuickAccessSectionNode: ReturnType<typeof getSectionNavigationNode> = yield select(getSectionNavigationNode, MenuPanelItemId.quickAccess);
    if (!oldQuickAccessSectionNode) {
        yield call(logger.error, "Cannot find navigation node for id: %s", MenuPanelItemId.quickAccess);
        return;
    }
    const oldUserSectionNode: ReturnType<typeof getSectionNavigationNode> = yield select(getSectionNavigationNode, MenuPanelItemId.user);
    if (!oldUserSectionNode) {
        yield call(logger.error, "Cannot find navigation node for id: %s", MenuPanelItemId.user);
        return;
    }
    const newQuickAccessSectionNode: ReturnType<typeof copyNode> = yield call(copyNode, oldQuickAccessSectionNode);
    const newUserSectionNode: ReturnType<typeof copyNode> = yield call(copyNode, oldUserSectionNode);
    // 2. propojení uzlů
    yield call(connectMultipleVertically, [newQuickAccessSectionNode, ...rootFormNodes, ...notificationGroupNodes, newUserSectionNode]);
    // 3. vložení do stavu
    yield call(setNavigationNodesForSectionSaga, MenuNavigationSectionType.panelForms, rootFormNodes, newQuickAccessSectionNode, MenuNavigationSectionType.static, EMPTY_ARRAY, newQuickAccessSectionNode.self.id);
    yield call(setNavigationNodesForSectionSaga, MenuNavigationSectionType.notificationGroups, notificationGroupNodes, newQuickAccessSectionNode, MenuNavigationSectionType.static, [newUserSectionNode], newQuickAccessSectionNode.self.id);
};

/**
 * Vytvoří a propojí navigační uzly pro menu panel.
 */
export const createMenuPanelNavigationNodesSaga: SagaWithReturnType = function* createMenuPanelNavigationNodesSaga() {
    const panelNavigationNodes: SagaYieldReturn<typeof createMultipleNavigationNodes> = yield call(createMultipleNavigationNodes, [
        MenuPanelItemId.logo,
        MenuPanelItemId.quickAccess,
        MenuPanelItemId.user,
        MenuPanelItemId.expandButton,
    ], MenuFocusableItemType.panelItem, MenuNavigationSectionType.static);
    yield call(connectMultipleVerticallyCyclic, panelNavigationNodes);
    yield put(createSetNavigationNodesAction(MenuNavigationSectionType.static, ...panelNavigationNodes));
};

/**
 * Vytvoří a propojí navigační uzly pro menu uživatele. Prerekvizitou je existence uzlu menu uživatele v menu panelu.
 */
export const createUserMenuNavigationNodesSaga: SagaWithReturnType = function* createUserMenuNavigationNodesSaga() {
    const userPanelItemNode: SagaYieldReturn<typeof getNavigationNode> = yield select(getNavigationNode, USER_MENU_FOCUSABLE_ID);
    invariant(userPanelItemNode, `[MainMenu] Cannot create navigation nodes for user menu, because node for menu panel item user menu (${USER_MENU_FOCUSABLE_ID}) does not exist.`);
    const newUserPanelItemNode: SagaYieldReturn<typeof copyNode> = yield call(copyNode, userPanelItemNode);

    const settingsItemNode: SagaYieldReturn<typeof createNavigationNode> = yield call(createNavigationNode, USER_MENU_SETTINGS_ITEM_ID, MenuFocusableItemType.userMenuItem, MenuNavigationSectionType.static);
    const themeItemNode: SagaYieldReturn<typeof createNavigationNode> = yield call(createNavigationNode, USER_MENU_TOGGLE_THEME_ITEM_ID, MenuFocusableItemType.userMenuItem, MenuNavigationSectionType.static);
    const logoutItemNode: SagaYieldReturn<typeof createNavigationNode> = yield call(createNavigationNode, USER_MENU_LOGOUT_ITEM_ID, MenuFocusableItemType.userMenuItem, MenuNavigationSectionType.static);
    const userMenuNodes = [settingsItemNode, themeItemNode, logoutItemNode];
    yield call(connectMultipleVerticallyCyclic, userMenuNodes);
    yield call(connectMultipleToLeftNode, userMenuNodes, newUserPanelItemNode);
    yield call(connectMultipleToEsc, userMenuNodes, newUserPanelItemNode);
    newUserPanelItemNode.rightId = settingsItemNode.self.id;
    yield put(createSetNavigationNodesAction(MenuNavigationSectionType.static, ...userMenuNodes, newUserPanelItemNode));
};

/**
 * Obsluhuje tvorbu a aktualizaci navigačních uzlů pro výsledky vyhledávání v sekci Rychlý přístup.
 */
export const createSearchResultsNavigationNodesSaga: SagaWithReturnType = function* createSearchResultsNavigationNodesSaga() {
    // 1. získání kořenového uzlu pro danou sekci navigace + kontrola
    const quickAccessSectionNode: ReturnType<typeof getNavigationNode> = yield select(getNavigationNode, QUICK_ACCESS_FOCUSABLE_ID);
    if (!quickAccessSectionNode) {
        yield call(logger.error, "Cannot find navigation node for id: %s", MenuPanelItemId.quickAccess);
        return;
    }

    const oldSearchInputNode: ReturnType<typeof getNavigationNode> = yield select(getNavigationNode, FORM_SEARCH_INPUT_FOCUSABLE_ID);
    if (!oldSearchInputNode) {
        yield call(logger.error, "Cannot find navigation node for id: %s", FORM_SEARCH_INPUT_FOCUSABLE_ID);
        return;
    }
    const newSearchInputNode: ReturnType<typeof copyNode> = yield call(copyNode, oldSearchInputNode);
    // 1.b kontrola aktivnosti vyhledávání
    const isSearchActive: ReturnType<typeof isSearchActiveSelector> = yield select(isSearchActiveSelector);
    if (!isSearchActive) {
        const firstQuickAccessFocusableItemId: ReturnType<typeof getFirstQuickAccessFocusableItemId> = yield select(getFirstQuickAccessFocusableItemId);
        const lastQuickAccessFocusableItemId: ReturnType<typeof getLastQuickAccessFocusableItemId> = yield select(getLastQuickAccessFocusableItemId);
        newSearchInputNode.upId = lastQuickAccessFocusableItemId;
        newSearchInputNode.downId = firstQuickAccessFocusableItemId;
        yield call(setNavigationNodesForSectionSaga, MenuNavigationSectionType.searchResults, EMPTY_ARRAY, newSearchInputNode, MenuNavigationSectionType.static);
        return;
    }
    // 2. Získání stavu vyhledávání a položek v pořadí vykreslení.
    const searchResults: ReturnType<typeof getSearchResults> = yield select(getSearchResults);
    // 3. vytvoření a propojení navigačních uzlů, napojení na kořenový uzel
    const state: IAppState = yield select();
    const searchResultNodes: ReturnType<typeof createSearchResultNavigationNodes> = yield call(createSearchResultNavigationNodes, searchResults, quickAccessSectionNode, newSearchInputNode, state);
    // 4. vyčištění stavu a vložení nových uzlů do stavu, kontrola validity zaměřené položky po aktualizaci
    yield call(setNavigationNodesForSectionSaga, MenuNavigationSectionType.searchResults, searchResultNodes, quickAccessSectionNode, MenuNavigationSectionType.static, [newSearchInputNode]);
};

/**
 * Obsluhuje tvorbu a aktualizaci navigačních uzlů pro menu formulářů.
 */
export const createFormsNavigationNodesSaga: SagaWithReturnType<[], readonly MutableNavigationNode[]> = function* createFormsNavigationNodesSaga() {
    // 1. získání položek v pořadí vykreslení
    const rootFormItemIds: ReturnType<typeof getAllowedRootFormItemIds> = yield select(getAllowedRootFormItemIds);
    // 2. pokud nejsou položky pro navigaci, vyčistit stav, kontrola validity zaměřené položky po aktualizaci
    const focusedNavigationNode: ReturnType<typeof getFocusedNavigationNode> = yield select(getFocusedNavigationNode);
    if (!rootFormItemIds || rootFormItemIds.length === 0) {
        yield put(createClearNavigationSectionAction(MenuNavigationSectionType.forms));
        yield call(checkValidityOfFocusedItemAfterFormNodesUpdateSaga, focusedNavigationNode);
        return EMPTY_ARRAY;
    }
    // 3. vytvoření a propojení navigačních uzlů, napojení na kořenový uzel
    const rootFormPanelItemIds = rootFormItemIds.map((formItemId) => createFormPanelItemId(formItemId));
    const rootFormNodes: ReturnType<typeof createMultipleNavigationNodes> = yield call(createMultipleNavigationNodes, rootFormPanelItemIds, MenuFocusableItemType.panelItem, MenuNavigationSectionType.panelForms);
    const state: IAppState = yield select();
    const formNodes: ReturnType<typeof createFormMenuNavigationNodes> = yield call(createFormMenuNavigationNodes, rootFormNodes, state);
    // 4. vyčištění stavu a vložení nových uzlů do stavu, kontrola validity zaměřené položky po aktualizaci
    yield put(createClearNavigationSectionAction(MenuNavigationSectionType.forms));
    yield put(createSetNavigationNodesAction(MenuNavigationSectionType.forms, ...formNodes));
    yield call(checkValidityOfFocusedItemAfterFormNodesUpdateSaga, focusedNavigationNode);
    return rootFormNodes;
};

/**
 * Provádí kontrolu zaměřeného prvku po aktualizaci navigačních uzlů formulářů mimo menu panel. Pokud byl původně zaměřený formulář, který po aktualizaci
 * již neexistuje, nastaví zaměření na položku aktivní sekce.
 * @param focusedNode
 */
export const checkValidityOfFocusedItemAfterFormNodesUpdateSaga: SagaWithReturnType<[NavigationNode | undefined]> = function* checkValidityOfFocusedItemAfterFormNodesUpdateSaga(
    focusedNode,
) {
    if (focusedNode?.self.section === MenuNavigationSectionType.forms) {
        const focusedNodeAfterUpdate = yield select(getFocusedNavigationNode);
        if (!focusedNodeAfterUpdate) {
            yield call(setFocusToActiveSectionSaga);
        }
    }
};

/**
 * Obsluhuje tvorbu navigačních uzlů pro notifikační skupiny.
 */
export const createNotificationGroupsNavigationNodesSaga: SagaWithReturnType<[], readonly MutableNavigationNode[]> = function* createNotificationGroupsNavigationNodesSaga() {
    // 2. získání položek v pořadí vykreslení
    const notificationGroupIds: ReturnType<typeof getNotificationGroupIds> = yield select(getNotificationGroupIds);
    // 4. vytvoření a propojení navigačních uzlů, napojení na sousední uzly
    const notificationGroupPanelItemIds = notificationGroupIds.map((notificationGroupId) => createNotificationGroupPanelItemId(notificationGroupId));
    const multipleNavigationNodes: SagaYieldReturn<typeof createMultipleNavigationNodes> = yield call(createMultipleNavigationNodes, notificationGroupPanelItemIds, MenuFocusableItemType.panelItem, MenuNavigationSectionType.notificationGroups);
    return multipleNavigationNodes;
};

/**
 * Obsluhuje tvorbu navigačních uzlů pro položky notifikací.
 */
export const createNotificationsNavigationNodesSaga: SagaWithReturnType = function* createNotificationsNavigationNodesSaga() {
    // 1. získání kořenového uzlu pro danou sekci navigace + kontrola
    const activeSectionId: ReturnType<typeof getActiveSectionId> = yield select(getActiveSectionId);
    if (!activeSectionId) {
        return;
    }
    const isNotificationGroupActive: ReturnType<typeof isNotificationGroupId> = yield select(isNotificationGroupPanelItemId, activeSectionId);
    if (!isNotificationGroupActive) {// pokud uživatel přepnul na jinou sekci, než se dokončilo načtení notifikací
        yield call(logger.warn, "Cannot create navigation nodes for notifications when notification group is not active.");
        return;
    }
    const oldNotificationGroupNode: ReturnType<typeof getSectionNavigationNode> = yield select(getSectionNavigationNode, activeSectionId);
    if (!oldNotificationGroupNode) {
        yield call(logger.error, "Cannot find navigation node for id: %s", activeSectionId);
        return;
    }
    const newNotificationGroupNode: ReturnType<typeof copyNode> = yield call(copyNode, oldNotificationGroupNode);
    // 2. získání položek v pořadí vykreslení
    const notificationItemIds: ReturnType<typeof getNotificationItemIds> = yield select(getNotificationItemIds);
    // 3. pokud nejsou položky pro navigaci, vyčistit stav a upravit kořenový uzel, kontrola validity zaměřené položky po aktualizaci
    if (notificationItemIds.length === 0) {
        newNotificationGroupNode.rightId = undefined;
        yield call(setNavigationNodesForSectionSaga, MenuNavigationSectionType.notifications, EMPTY_ARRAY, newNotificationGroupNode, MenuNavigationSectionType.notificationGroups);
        return;
    }
    // 4. vytvoření a propojení navigačních uzlů, napojení na kořenový uzel
    const showAllNotificationsButtonNode: SagaYieldReturn<typeof createNavigationNode> = yield call(createNavigationNode, SHOW_ALL_NOTIFICATIONS_BUTTON_ID, MenuFocusableItemType.showAllNotificationsButton, MenuNavigationSectionType.static);
    const setGroupNotificationsAsReadButtonNode: SagaYieldReturn<typeof createNavigationNode> = yield call(createNavigationNode, SET_GROUP_NOTIFICATIONS_AS_READ_BUTTON_ID, MenuFocusableItemType.setGroupNotificationsAsReadButton, MenuNavigationSectionType.static);
    const notificationNodes: ReturnType<typeof createNotificationMenuNavigationNodes> = yield call(createNotificationMenuNavigationNodes, notificationItemIds, newNotificationGroupNode);
    yield call(connectVertically, setGroupNotificationsAsReadButtonNode, notificationNodes[0]);
    yield call(connectVertically, showAllNotificationsButtonNode, setGroupNotificationsAsReadButtonNode);
    yield call(connectVertically, notificationNodes[notificationNodes.length - 1], showAllNotificationsButtonNode);
    // 5. vyčištění stavu a vložení nových uzlů do stavu, kontrola validity zaměřené položky po aktualizaci
    yield call(setNavigationNodesForSectionSaga, MenuNavigationSectionType.notifications, notificationNodes, newNotificationGroupNode, MenuNavigationSectionType.notificationGroups, [showAllNotificationsButtonNode, setGroupNotificationsAsReadButtonNode]);
    // 6. pokud je zaměřená sekce skupiny notifikací, přepne zaměření na její první položku
    const focusedItemId: ReturnType<typeof getFocusedItemId> = yield select(getFocusedItemId);
    if (focusedItemId === newNotificationGroupNode.self.id && newNotificationGroupNode.rightId) {
        yield put(createSetFocusedItemAction(newNotificationGroupNode.rightId));
    }
};

/**
 * Nastaví zadané navigační uzly do stavu. Před nastavením smaže původní uzly.
 * Provede kontrolu aktuálně zaměřeného prvku. Pokud byl zaměřený prvek při aktualizaci
 * smazán, nastaví zaměření buď na pravého souseda rodičovského uzlu, nebo pokud neexistuje,
 * tak na rodičovský uzel samotný.
 * @param section Sekce, do které se vkládají uzly.
 * @param sectionNodes Uzly, které se nastaví do zadané sekce.
 * @param panelItemNode Rodičovský uzel z levého menu panelu sekcí, který se aktualizuje.
 * @param panelItemNodeSection Sekce, do které se vkládá rodičovský uzel. Nemůže být získáno z uzlu, protože ten může obsahovat i dynamickou sekci. Dynamické sekce se
 * ale pro ukládání nepoužívají.
 * @param additionalStaticNodes Doplňující uzly, které se vloží do statické sekce.
 * @param fallbackFocusedItemId Id zaměřitelné položky, která se nastaví jako zaměřená, pokud při aktualizaci uzlů byla původní zaměřená položka smazána.
 * Pokud není tento parametr zadaný:
 * - a) Použije se pravý soused uzlu panelItem, pokud takový soused existuje a pokud je menu expandované.
 * - b) Použije se panelItem samotný, pokud takový soused neexistuje nebo je menu skryté.
 */
export const setNavigationNodesForSectionSaga: SagaWithReturnType<[NavigationSection, readonly NavigationNode[], NavigationNode, NavigationSection, (readonly NavigationNode[])?, FocusableItemId?]> = function* setNavigationNodesForSectionSaga(
    section,
    sectionNodes,
    panelItemNode,
    panelItemNodeSection,
    additionalStaticNodes = EMPTY_ARRAY,
    fallbackFocusedItemId?,
) {
    const focusedItemSection: ReturnType<typeof getFocusedItemStoreSection> = yield select(getFocusedItemStoreSection);
    yield put(createClearNavigationSectionAction(section));
    yield put(createSetNavigationNodesAction(section, ...sectionNodes));
    yield put(createSetNavigationNodesAction(panelItemNodeSection, panelItemNode));
    if (additionalStaticNodes && additionalStaticNodes.length > 0) {
        yield put(createSetNavigationNodesAction(MenuNavigationSectionType.static, ...additionalStaticNodes));
    }

    if (focusedItemSection === section) {
        const focusedNode: ReturnType<typeof getFocusedNavigationNode> = yield select(getFocusedNavigationNode);
        if (!focusedNode) {
            const menuIsExpanded: ReturnType<typeof isMenuExpanded> = yield select(isMenuExpanded);
            const newFocusedItemId = fallbackFocusedItemId || (menuIsExpanded && panelItemNode.rightId) || panelItemNode.self.id;
            yield put(createSetFocusedItemAction(newFocusedItemId));
        }
    }
    yield put(createNavigationNodesUpdatedAction(section));
};

/**
 * Řeší synchronizaci zaměření v prohlížeči se stavem v reduxu při kliknutí myší na akční položky (u kterých je třeba to řešit).
 */
export const actionItemMouseFocusSaga: SagaWithReturnType = function* actionItemMouseFocusSaga() {
    const { openProfileActionItem, openProfileInTabActionItem, openFormActionItem, openFormInTabActionItem, openFormInstanceActionItem, openFormInstanceInTabActionItem, formFavoriteActionItem, openedInstanceFavoriteActionItem, closeActionItem } = MenuItemActivatedType;
    yield takeLatest(createMenuItemActivatedByMousePattern([
        openProfileActionItem, openProfileInTabActionItem, openFormActionItem, openFormInTabActionItem, openFormInstanceActionItem, openFormInstanceInTabActionItem, formFavoriteActionItem, openedInstanceFavoriteActionItem,
    ]), focusActionItemAndParentSaga);
    yield takeLatest(createMenuItemActivatedByMousePattern([closeActionItem]), focusActionItemParentSaga);
};

/**
 * Nastaví zaměření na rodiče akční položky.
 * @param action Akce aktivace položky menu.
 */
export const focusActionItemParentSaga: SagaWithReturnType<[IMenuItemActivatedAction]> = function* focusActionItemParentSaga(action) {
    const focusableItemId: SagaYieldReturn<typeof menuItemToFocusableItemSaga> = yield call(menuItemToFocusableItemSaga, action);
    if (!focusableItemId) {
        return;
    }
    const clickedNode: ReturnType<typeof getNavigationNode> = yield select(getNavigationNode, focusableItemId);
    if (clickedNode?.escId) {
        yield put(createSetFocusedItemAction(clickedNode.escId));
    }
};

/**
 * Nastaví zaměření na akční položku a následně na její rodiče.
 * @param action Akce aktivace položky menu.
 */
export const focusActionItemAndParentSaga: SagaWithReturnType<[IMenuItemActivatedAction]> = function* focusActionItemAndParentSaga(action) {
    const focusableItemId: SagaYieldReturn<typeof menuItemToFocusableItemSaga> = yield call(menuItemToFocusableItemSaga, action);
    if (!focusableItemId) {
        return;
    }
    yield put(createSetFocusedItemAction(focusableItemId));
    const actionItemFocusedAction: IActionItemFocusedAction = yield take(ACTION_ITEM_FOCUSED);
    if (actionItemFocusedAction.payload.focusableItemId !== focusableItemId) {
        return;
    }
    const clickedNode: ReturnType<typeof getNavigationNode> = yield select(getNavigationNode, focusableItemId);
    if (clickedNode?.escId) {
        yield put(createSetFocusedItemAction(clickedNode.escId));
    }
};

/**
 * Vytvoří id pro zaměřitelnou položku z kombinace typu a id položky. Pokud se jedná o otevřenou položku s definovaným
 * id instance, k vytvoření se použije toto id.
 * @param action
 * @return Vrací id zaměřitelné položky.
 */
export const menuItemToFocusableItemSaga: SagaWithReturnType<[IMenuItemActivatedAction], FocusableItemId | undefined> = function* menuItemToFocusableItemSaga(action) {
    const { itemId, itemType, section } = action.payload;
    const focusableItemType: ReturnType<typeof menuItemActivatedTypeToFocusableItemType> = yield call(menuItemActivatedTypeToFocusableItemType, itemType);
    if (!focusableItemType) {
        logger.warn("Focusable item type not found. Item type: " + itemType);
        return undefined;
    }
    const focusableItemId: FocusableItemId = yield call(createFocusableItemId, itemId, focusableItemType, section);
    return focusableItemId;
};

/**
 * Vzory akcí, které mají vliv na data formulářů, podle kterých se generují jejich navigační uzly.
 */
const sharedFormActionPatterns = [createAreLocalizationKeysLoadedActionPattern(FETCHING_KEY_FORM_ITEMS_LOCALIZATION), SET_FORM_ITEM_ACTION_RESTRICTIONS, SET_PROFILE_ACTION_RESTRICTIONS];

/**
 * Výchozí nastavení navigace.
 *
 * (Nemůže být v ./constants.ts, protože obsahuje reference na ságy)
 */
export const DEFAULT_NAVIGATION_CONFIG: CompleteNavigationConfig = {
    enableNavigation: true,
    optionalFeatures: {
        enableAutoFocusOpenedForms: true,
        enableMenuAutoClose: true,
        enableSubmenuAutoOpenAndClose: true,
    },
    nodeDefinitions: {
        menuPanel: {
            staticSaga: createMenuPanelNavigationNodesSaga,
            dynamic: {
                actionPatterns: [
                    ...sharedFormActionPatterns,
                    SET_MENU_ITEMS,
                    SET_NOTIFICATION_GROUPS,
                ],
                saga: createFormsAndNotificationGroupsNavigationNodesSaga,
            },
        },
        quickAccess: {
            staticSaga: createStaticQuickAccessNavigationNodesSaga,
            dynamic: {
                actionPatterns: [
                    ...sharedFormActionPatterns,
                    HISTORY_FORMS_INFO_UPDATED,
                    formOpenedAndShowInMenuActionPattern,
                    FORM_CLOSED,
                    FORM_ITEM_INSTANCE_CLOSE_RESTRICTIONS_UPDATED,
                    FAVORITE_FORMS_LOADED,
                    FAVORITE_FORM_UPDATED,
                ],
                saga: createQuickAccessNavigationNodesSaga,
            },
        },
        searchResults: {
            dynamic: {
                actionPatterns: [
                    SET_SEARCH_RESULTS,
                ],
                saga: createSearchResultsNavigationNodesSaga,
            },
        },
        notificationList: {
            dynamic: {
                actionPatterns: [
                    RESET_NOTIFICATION_ITEMS,
                    CONCAT_NOTIFICATION_ITEMS,
                ],
                saga: createNotificationsNavigationNodesSaga,
            },
        },
        userMenu: {
            staticSaga: createUserMenuNavigationNodesSaga,
        },
    },
};
